import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BiPhone } from "react-icons/bi";
import { io } from 'socket.io-client';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
 
const CallPage = () => {
    const [showKeypad, setShowKeypad] = useState(false);
    const [callStatus, setCallStatus] = useState('ringing');
    const [callTime, setCallTime] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const { name, number } = useParams();
    const [showCallEndPopup, setShowCallEndPopup] = useState(false);
    const [transcriptions, setTranscriptions] = useState([]);
    const [sentiment, setSentiment] = useState('Neutral'); // Fixed initial state
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem('user')) || {};
    useEffect(() => {
        const socket = io(process.env.REACT_APP_SERVER_API);

        socket.on('connect', () => console.log('WebSocket connected'));
        socket.on('connect_error', (error) => console.error('WebSocket connection error:', error));

        socket.on('callStatusUpdate', ({ CallSid, CallStatus }) => {
            if (CallSid === localStorage.getItem('currentCallSid')) {
                console.log('Received call status update:', { CallSid, CallStatus });
                setCallStatus(CallStatus);
                if (CallStatus === 'connected') {
                    startTimeRef.current = new Date().toISOString();
                } else if (CallStatus === 'completed') {
                    endTimeRef.current = new Date().toISOString();
                    setShowCallEndPopup(true);
                    handleCallEnd();
                    setTimeout(() => setShowCallEndPopup(false), 1000);
                } else if (CallStatus === 'transferred') {
                    setShowCallEndPopup(true);
                    setTimeout(() => {
                        setShowCallEndPopup(false);
                        navigate('/dashboard');
                    }, 2000);
                } else if (['no-answer', 'busy', 'failed'].includes(CallStatus)) {
                    addToWaitingList(CallSid, CallStatus);
                }
            }
        });

        socket.on('transcription', ({ callSid, text }) => {
            if (callSid === localStorage.getItem('currentCallSid')) {
                setTranscriptions((prev) => [...prev, text]);
            }
        });

        socket.on('sentimentUpdate', ({ callSid, sentiment }) => {
            if (callSid === localStorage.getItem('currentCallSid')) {
                console.log('Received sentiment update:', sentiment);
                setSentiment(sentiment);
            }
        });

        return () => socket.disconnect();
    }, []);

    const handleCallEnd = () => {
        setShowLoader(true);
        saveCallHistory();
        setTimeout(() => setShowLoader(false), 15000);
    };

    useEffect(() => {
        let interval;
        if (callStatus === 'connected') {
            interval = setInterval(() => setCallTime((prev) => prev + 1), 1000);
        }
        return () => clearInterval(interval);
    }, [callStatus]);

    const formatToIST = (utcTime) => {
        return new Date(utcTime).toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata'
        });
    };

    const saveCallHistory = async () => {
        try {
            const callId = localStorage.getItem('currentCallId');
            if (!callId || !startTimeRef.current || !endTimeRef.current) {
                console.error("Missing callId or times");
                return;
            }

            const payload = {
                startTime: formatToIST(startTimeRef.current),
                endTime: formatToIST(endTimeRef.current),
                summary: callStatus === 'transferred' ? "Call transferred to executive" : "Call completed",
                transcription: transcriptions.join('\n'),
                agentSentiment: sentiment,
            };

            await axios.put(`${process.env.REACT_APP_SERVER_API}/api/call-history/${callId}`, payload);
        } catch (error) {
            console.error("Error updating call history:", error);
        }
    };

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const BackButton = () => navigate('/dashboard');

    const addToWaitingList = async (callSid, status) => {
        try {
            const payload = {
                callSid,
                from: decodeURIComponent(name), // Assuming 'name' is the agent or caller
                to: decodeURIComponent(number), // The contact being called
                status: status === 'no-answer' ? 'no-answer' : 'disconnected',
                userId: user.id,
            };

            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_API}/api/waiting-list`,
                payload
            );
            console.log('Added to waiting list:', response.data);
        } catch (error) {
            console.error('Error adding to waiting list:', error);
        }
    };

    const endCall = async () => {
        const callSid = localStorage.getItem('currentCallSid');
        if (!callSid) return console.error('CallSid not found');
        try {
            await axios.post(`${process.env.REACT_APP_SERVER_API}/api/end-call`, { callSid });
            setCallStatus('completed');
        } catch (error) {
            console.error('Error ending call:', error);
        } finally {
            window.close();
        }
    };

    const Keypad = () => (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="grid grid-cols-3 gap-6 p-8 w-full max-w-[300px] mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((num) => (
                <button
                    key={num}
                    className="bg-gray-100 text-gray-800 rounded-full h-14 w-14 flex items-center justify-center hover:bg-gray-200 transition-colors border border-gray-200 shadow-sm text-xl"
                >
                    {num}
                </button>
            ))}
        </motion.div>
    );

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'Positive': return 'bg-green-500';
            case 'Negative': return 'bg-red-500';
            case 'Negative/DND': return 'bg-red-700';
            case 'Neutral': return 'bg-gray-500';
            default: return 'bg-gray-500';
        }
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {showCallEndPopup && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-xl text-lg font-semibold"
                >
                    📞 {callStatus === 'transferred' ? 'Call Transferred' : 'Call Ended'}
                </motion.div>
            )}
            {showLoader && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-white text-lg">Storing call logs...</p>
                    </div>
                </div>
            )}

            <div className="flex-1 max-w-2xl mx-auto p-4 relative flex items-center justify-center">
                <div className="bg-white rounded-[40px] shadow-xl p-6 w-full max-w-[360px] h-[90vh] border-8 border-black relative overflow-hidden">
                    <div className="h-6 w-32 bg-black rounded-full mx-auto -mt-4 mb-8" />
                    <div className="flex justify-center mb-6">
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                            <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                    </div>
                    <div className="text-center mb-6">
                        <h1 className="text-2xl font-semibold text-gray-800">{decodeURIComponent(name)}</h1>
                        <p className="text-gray-600 text-lg">{decodeURIComponent(number)}</p>
                        <div className="mt-2 text-gray-500 text-base">
                            {callStatus === 'ringing' && 'Ringing...'}
                            {callStatus === 'connected' && formatTime(callTime)}
                            {callStatus === 'transferred' && 'Transferred to Executive'}
                        </div>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <button
                            onClick={() => setShowKeypad(!showKeypad)}
                            className="mb-4 bg-gray-100 px-6 py-3 rounded-full hover:bg-gray-200 transition-colors shadow-sm"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        {showKeypad && <Keypad />}
                    </div>
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                        <button
                            className="bg-red-600 text-white rounded-full h-20 w-20 flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                            onClick={endCall}
                        >
                            <BiPhone className="w-10 h-10 text-white rotate-135 transform" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-white p-8 border-l border-gray-200 hidden lg:block">
                <button onClick={() => navigate('/dashboard')} className='bg-blue-600 text-white rounded-full h-10 w-60'> Back to Dashboard </button>
                <div className="bg-white rounded-xl h-full p-6 shadow-lg">
                    <h2 className="text-gray-800 text-xl font-semibold mb-4">Live Transcription</h2>
                    <div className="mb-4">
                        <motion.span
                            key={sentiment}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className={`inline-block px-4 py-2 rounded-full text-white text-lg font-semibold ${getSentimentColor(sentiment)}`}
                        >
                            Sentiment: {sentiment}
                        </motion.span>
                    </div>
                    <div className="h-[75%] bg-gray-50 rounded-lg p-4 text-gray-600 border border-gray-200 overflow-auto">
                        {transcriptions.length === 0 ? (
                            <div className="text-center text-gray-400 py-8">
                                Transcription will appear here in real-time...
                            </div>
                        ) : (
                            <AnimatePresence>
                                {transcriptions.map((text, index) => (
                                    <motion.p
                                        key={index}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.5 }}
                                        className="mb-2"
                                    >
                                        {text}
                                    </motion.p>
                                ))}
                            </AnimatePresence>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CallPage;